// Booking-page promises: step 2 (welcome right after booking) + step 3 (arrival pack D-2) — status, pause switch, run now.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, MessageCircle, Pause, Play, RefreshCw, ShieldCheck } from 'lucide-react';
import { apiCall } from './_shared';

const KINDS = [
  { key: 'booking_welcome', title: 'Step 2 · Personal welcome after booking', when: 'within minutes of a new Beds24 booking (webhook) · safety net every 20 min' },
  { key: 'arrival_pack', title: 'Step 3 · Arrival pack', when: 'two days before arrival, 09:30 — directions, free parking, check-in times, what to bring, online check-in link' },
];

const PromisesCard = ({ canEdit, onPreview }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => { try { setSt(await apiCall('/api/admin/promises/status')); } catch (e) { setMsg(e.message || 'Failed to load'); } }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async () => {
    setBusy(true);
    try { await apiCall(`/api/admin/promises/pause?paused_flag=${!st.paused}`, { method: 'POST' }); await load(); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/promises/run', { method: 'POST' });
      setMsg(`Welcomes: ${r.welcomes.sent} sent / ${r.welcomes.checked} checked · Arrival packs for ${r.arrival_packs.target}: ${r.arrival_packs.sent} sent / ${r.arrival_packs.checked} checked`);
      await load();
    } catch (e) { setMsg(e.message || 'Run failed'); } finally { setBusy(false); }
  };
  if (!st) return <div className="bg-white rounded-xl border p-4 text-sm text-gray-500" data-testid="promises-loading">Loading booking promises…</div>;
  const kindRows = KINDS.map((k) => {
    const s = st.kinds[k.key] || {};
    const langButtons = ['en', 'me', 'sq', 'de', 'ru'].map((l) => (
      <button key={l} type="button" onClick={() => onPreview(k.key, l)} className="text-[11px] bg-gray-100 hover:bg-gray-200 px-2 py-0.5 rounded uppercase" data-testid={`promises-preview-${k.key}-${l}`}>{l}</button>
    ));
    return (
      <div key={k.key} className="rounded-lg border p-3" data-testid={`promises-kind-${k.key}`}>
        <p className="font-semibold text-gray-900 text-sm">{k.title}</p>
        <p className="text-xs text-gray-500">{k.when}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
          <span data-testid={`promises-${k.key}-emails`}><b>{s.emails_30d}</b> e-mails · 30 d</span>
          <span data-testid={`promises-${k.key}-wa`}><b>{s.wa_sent_30d}</b> WhatsApp sent · <b className={s.wa_pending ? 'text-amber-600' : ''}>{s.wa_pending}</b> waiting for reception tap</span>
          <span className="inline-flex items-center gap-1">Preview: {langButtons}</span>
        </div>
      </div>
    );
  });
  const pendingRows = (st.pending || []).slice(0, 8).map((p) => (
    <li key={p.id} className="text-xs text-gray-700" data-testid={`promises-pending-${p.id}`}>{p.guest_name} · {p.kind === 'booking_welcome' ? 'welcome' : 'arrival pack'} · {p.phone} · {p.lang?.toUpperCase()}</li>
  ));
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${st.paused ? 'bg-amber-50 border-amber-300' : 'bg-white'}`} data-testid="promises-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-orange-500" /> Booking-page promises</h3>
          <p className="text-xs text-gray-500 mt-0.5">The "What happens after you book" steps guests see at checkout — sent automatically, each exactly once, in the guest's language. E-mail always; WhatsApp {st.whatsapp_configured ? 'automatically via Twilio' : 'as a one-tap wa.me task in Reception → Auto messages until Twilio is connected'}.</p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <button type="button" onClick={toggle} disabled={busy} className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold border ${st.paused ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-800'}`} data-testid="promises-toggle">
              {st.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />} {st.paused ? 'Resume automatic sending' : 'Pause automatic sending'}
            </button>
          )}
          <button type="button" onClick={run} disabled={busy} className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold bg-orange-500 text-white disabled:opacity-50" data-testid="promises-run">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run now
          </button>
        </div>
      </div>
      {st.paused && <p className="text-xs font-semibold text-amber-800" data-testid="promises-paused-note">Paused — nothing is sent automatically on this server. "Run now" still sends.</p>}
      <div className="grid md:grid-cols-2 gap-3">{kindRows}</div>
      {pendingRows.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="promises-pending">
          <p className="text-xs font-semibold text-amber-800 inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp messages waiting for a reception tap (Reception → Auto messages)</p>
          <ul className="mt-1 space-y-0.5">{pendingRows}</ul>
        </div>
      )}
      {msg && <p className="text-xs text-gray-700" data-testid="promises-msg">{msg}</p>}
    </div>
  );
};

export default PromisesCard;
